import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DatacircleLogoMark } from '../brand/DatacircleLogoMark';
import LoginModal from '../Auth/LoginModal';
import { useAuth } from '../../contexts/AuthContext';
import { privateCircleService } from '../../services/privateCircleService';
import {
  communityPathForStatus,
  fetchCommunityMembershipStatus,
  type CommunityMembershipStatus,
} from '../../services/communityMembershipService';
import { fetchPublicGradingDemoQuestions } from '../../services/gradingService';
import {
  loadMarketProfileForUser,
  persistMarketProfile,
} from '../Community/marketProfile/marketProfileStore';
import { CustomSelect } from '../common/CustomSelect';
import PhoneInput, { isCompleteInternationalPhone } from '../common/PhoneInput';
import './PrivateCirclePage.css';

type IconName =
  | 'chart'
  | 'shield'
  | 'lock'
  | 'users'
  | 'layers'
  | 'compass'
  | 'file'
  | 'phone'
  | 'badge'
  | 'network'
  | 'spark';

type ApplicationFormState = {
  firstName: string;
  lastName: string;
  nickname: string;
  email: string;
  company: string;
  title: string;
  sector: string;
  phone: string;
};

type AuthUser = NonNullable<ReturnType<typeof useAuth>['user']>;

function mergeApplicationFormWithUser(
  prev: ApplicationFormState,
  user: AuthUser,
  identityEmail?: string,
): ApplicationFormState {
  const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
  const [firstName = '', lastName = ''] = fullName.split(/\s+/);
  const accountEmail = (identityEmail?.trim() || user.email || prev.email).trim();
  return {
    ...prev,
    email: accountEmail,
    firstName: prev.firstName || firstName,
    lastName: prev.lastName || lastName,
    phone: prev.phone || user.phone || '',
  };
}

const coverageTags = ['IB', 'M&A', 'PE', 'VC', 'FO', 'Corporate VC'];

const sectors = ['Investment Banking', 'M&A', 'Private Equity', 'Venture Capital', 'Family Office', 'Corporate VC', 'Corporate investments'];

const benchmarkRows = [
  { label: 'FIX', position: 52 },
  { label: 'STI', position: 68 },
  { label: 'LTIP', position: 72 },
  { label: 'Total Comp', position: 78 },
];

const roleGradingDemo = {
  formalTitle: 'Ассоциат',
  formalMeta: 'Формальный титул в компании',
  referenceTitle: 'Вице-президент',
  referenceMeta: 'Референтная должность обзора для сравнения FIX, STI, LTIP и Total Comp',
};

const insightCards = [
  {
    tag: 'PE · директор',
    question: 'Какой STI считается рыночным для инвестиционного директора в фонде среднего размера?',
    meta: '12 релевантных ответов · анонимно',
  },
  {
    tag: 'IB · ассоциат/вице-президент',
    question: 'Когда роль Вице-президент фактически соответствует уровню Директора?',
    meta: '8 комментариев · модерируемо',
  },
  {
    tag: 'VC · принципал',
    question: 'Как сравнивать carry между фондами разного размера?',
    meta: '6 проверенных участников · анонимно',
  },
];

const NAV_SCROLL_OFFSET = 96;

function scrollToSection(sectionId: string) {
  const el = document.getElementById(sectionId);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - NAV_SCROLL_OFFSET;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

const faqItems = [
  {
    question: 'Нужно ли раскрывать имя внутри сообщества?',
    answer: 'Нет. Все профили участников строго анонимизируются.',
  },
  {
    question: 'Что происходит после заявки?',
    answer: 'Мы проверяем профиль, профессиональный контекст и релевантность участия. В некоторых случаях проводится короткий вводный звонок.',
  },
  {
    question: 'Будут ли видны мои компенсационные данные?',
    answer: 'Нет. Индивидуальные данные не отображаются как открытые записи и используются только для агрегированных рыночных ориентиров.',
  },
  {
    question: 'Это сообщество только для IB?',
    answer: 'Нет. Фокус шире: IB, M&A, PE, VC, FO, Corporate VC.',
  },
];

function Icon({ name, className }: { name: IconName; className?: string }) {
  const common = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
  };

  const stroke = {
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (name) {
    case 'chart':
      return (
        <svg {...common}>
          <path {...stroke} d="M4 19V5" />
          <path {...stroke} d="M4 19h16" />
          <path {...stroke} d="M8 16v-5" />
          <path {...stroke} d="M12 16V8" />
          <path {...stroke} d="M16 16v-7" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...common}>
          <path {...stroke} d="M12 3 5 6v5c0 4.7 2.9 8.2 7 10 4.1-1.8 7-5.3 7-10V6l-7-3Z" />
          <path {...stroke} d="m9.5 12 1.7 1.7 3.5-4" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...common}>
          <path {...stroke} d="M8 11V8a4 4 0 0 1 8 0v3" />
          <path {...stroke} d="M6.5 11h11v8h-11z" />
          <path {...stroke} d="M12 15v1.8" />
        </svg>
      );
    case 'users':
      return (
        <svg {...common}>
          <path {...stroke} d="M16 19c0-2.2-1.8-4-4-4s-4 1.8-4 4" />
          <path {...stroke} d="M12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
          <path {...stroke} d="M20 18c0-1.8-1.2-3.2-3-3.8" />
          <path {...stroke} d="M17 6.4a2.5 2.5 0 0 1 0 4.8" />
        </svg>
      );
    case 'layers':
      return (
        <svg {...common}>
          <path {...stroke} d="m12 3 8 4-8 4-8-4 8-4Z" />
          <path {...stroke} d="m4 12 8 4 8-4" />
          <path {...stroke} d="m4 17 8 4 8-4" />
        </svg>
      );
    case 'compass':
      return (
        <svg {...common}>
          <path {...stroke} d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" />
          <path {...stroke} d="m15.5 8.5-2 5-5 2 2-5 5-2Z" />
        </svg>
      );
    case 'file':
      return (
        <svg {...common}>
          <path {...stroke} d="M7 3h7l4 4v14H7V3Z" />
          <path {...stroke} d="M14 3v5h5" />
          <path {...stroke} d="M9.5 13h5" />
          <path {...stroke} d="M9.5 17h4" />
        </svg>
      );
    case 'phone':
      return (
        <svg {...common}>
          <path {...stroke} d="M5 5.5c0 7.5 6 13.5 13.5 13.5.8 0 1.5-.7 1.5-1.5v-2.2l-4-1.6-1.7 1.7a12 12 0 0 1-5.7-5.7l1.7-1.7L8.7 4H6.5C5.7 4 5 4.7 5 5.5Z" />
        </svg>
      );
    case 'badge':
      return (
        <svg {...common}>
          <path {...stroke} d="M12 3 14.5 8l5.5.8-4 3.9.9 5.5L12 15.6 7.1 18.2l.9-5.5-4-3.9 5.5-.8L12 3Z" />
        </svg>
      );
    case 'network':
      return (
        <svg {...common}>
          <path {...stroke} d="M7 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
          <path {...stroke} d="M17 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
          <path {...stroke} d="M12 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
          <path {...stroke} d="M8.8 7 15 8" />
          <path {...stroke} d="m15.8 10.8-2.4 4.6" />
          <path {...stroke} d="m8.3 9.2 2.2 5.8" />
        </svg>
      );
    case 'spark':
      return (
        <svg {...common}>
          <path {...stroke} d="M12 3v4" />
          <path {...stroke} d="M12 17v4" />
          <path {...stroke} d="M3 12h4" />
          <path {...stroke} d="M17 12h4" />
          <path {...stroke} d="m6 6 2.7 2.7" />
          <path {...stroke} d="m15.3 15.3 2.7 2.7" />
          <path {...stroke} d="m18 6-2.7 2.7" />
          <path {...stroke} d="m8.7 15.3-2.7 2.7" />
        </svg>
      );
    default:
      return null;
  }
}

function SectionIntro({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle?: string }) {
  return (
    <div className="dcc-section-intro">
      {eyebrow && <span className="dcc-eyebrow">{eyebrow}</span>}
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}

function HeaderNav() {
  const navItems = [
    { label: 'Что внутри', sectionId: 'product' },
    { label: 'Компенсация', sectionId: 'benchmarks' },
    { label: 'Уровень роли', sectionId: 'roles' },
    { label: 'Peer insights', sectionId: 'insights' },
    { label: 'Участники', sectionId: 'members' },
    { label: 'Доступ', sectionId: 'admission' },
    { label: 'Конфиденциальность', sectionId: 'trust' },
    { label: 'Кому подходит', sectionId: 'eligibility' },
  ];

  return (
    <header className="dcc-nav">
      <Link className="dcc-nav__brand" to="/" aria-label="datacircle — на главную">
        <DatacircleLogoMark size={34} />
        <span className="dcc-nav__brand-text">datacircle</span>
        <span className="dcc-nav__label">Community Intelligence</span>
      </Link>
      <nav className="dcc-nav__links" aria-label="Навигация по странице">
        {navItems.map((item) => (
          <button key={item.sectionId} type="button" onClick={() => scrollToSection(item.sectionId)}>
            {item.label}
          </button>
        ))}
      </nav>
      <button type="button" className="dcc-nav__cta" onClick={() => scrollToSection('application')}>
        Подать заявку
      </button>
    </header>
  );
}

function ApplicationForm() {
  const { user, loading: authLoading, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState<CommunityMembershipStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [form, setForm] = useState<ApplicationFormState>({
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    company: '',
    title: '',
    sector: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      setMembershipStatus(null);
      return;
    }
    let cancelled = false;
    setStatusLoading(true);
    (async () => {
      const state = await fetchCommunityMembershipStatus();
      if (cancelled) return;
      setMembershipStatus(state?.status ?? 'none');
      setStatusLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    setForm((prev) => mergeApplicationFormWithUser(prev, user));
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleLoginSubmit = async (email: string, password: string): Promise<boolean> => {
    try {
      await signIn(email, password);
      setForm((prev) => ({ ...prev, email: email.trim() }));
      setIsLoginOpen(false);
      return true;
    } catch {
      return false;
    }
  };

  const handleRegisterSubmit = async (name: string, email: string, password: string) => {
    const result = await signUp(name, email, password);
    const accountEmail = (result.user?.email || email).trim();
    if (result.user) {
      setForm((prev) => mergeApplicationFormWithUser(prev, result.user!, accountEmail));
    } else {
      setForm((prev) => ({ ...prev, email: accountEmail }));
    }
    setIsLoginOpen(false);
    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.firstName || !form.lastName || !form.nickname || !form.sector) {
      setError('Все поля должны быть заполнены');
      return;
    }
    if (!isCompleteInternationalPhone(form.phone)) {
      setError('Укажите номер телефона полностью');
      return;
    }

    setLoading(true);
    try {
      const result = await privateCircleService.apply({
        first_name: form.firstName.trim(),
        second_name: form.lastName.trim(),
        nickname: form.nickname.trim(),
        company: form.company.trim() || undefined,
        title: form.title.trim() || undefined,
        sector: form.sector.trim() || undefined,
        phone: form.phone.trim() || undefined,
      });

      if (result.success) {
        if (user?.email) {
          const accountEmail = user.email.trim();
          const current = loadMarketProfileForUser(accountEmail, user.id);
          const data = current.userProfile.data;
          void persistMarketProfile(
            {
              ...current,
              userProfile: {
                ...current.userProfile,
                data: {
                  ...data,
                  email: accountEmail,
                  pseudonym: form.nickname.trim() || data.pseudonym,
                  firstName: form.firstName.trim() || data.firstName,
                  lastName: form.lastName.trim() || data.lastName,
                  companyName: form.company.trim() || data.companyName,
                  sectorName: form.sector.trim() || data.sectorName,
                  positionName: form.title.trim() || data.positionName,
                  phone: form.phone.trim() || data.phone,
                },
                updatedAt: new Date().toISOString(),
              },
            },
            accountEmail,
            user.id,
          ).catch(() => undefined);
        }
        navigate(communityPathForStatus('pending'));
      } else {
        setError(result.message);
      }
    } catch {
      setError('Не удалось отправить заявку. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || (user && statusLoading)) {
    return (
      <div className="dcc-application" id="application">
        <p className="dcc-application__loading">Загрузка...</p>
      </div>
    );
  }

  if (user && membershipStatus === 'pending') {
    return (
      <div className="dcc-application dcc-application--success" id="application">
        <div className="dcc-success-icon">
          <Icon name="shield" />
        </div>
        <h2>Заявка уже подана</h2>
        <p>
          Наши аналитики проверят данные и свяжутся с Вами для уточнения информации. Обычно это занимает не больше одного дня.
        </p>
        <button
          className="dcc-button dcc-button--primary"
          type="button"
          onClick={() => navigate(communityPathForStatus('pending'))}
        >
          Статус заявки
        </button>
      </div>
    );
  }

  if (user && membershipStatus === 'approved') {
    return (
      <div className="dcc-application dcc-application--success" id="application">
        <div className="dcc-success-icon">
          <Icon name="lock" />
        </div>
        <h2>Доступ открыт</h2>
        <p>Вы уже являетесь участником сообщества datacircle.</p>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <div className="dcc-application dcc-application--auth-gate" id="application">
          <div className="dcc-application__header">
            <h2>Оставьте заявку на доступ</h2>
            <p>
              Подать заявку могут только зарегистрированные пользователи datacircle. Войдите в аккаунт или создайте новый, чтобы продолжить.
            </p>
          </div>
          <button
            className="dcc-button dcc-button--primary"
            type="button"
            onClick={() => setIsLoginOpen(true)}
          >
            Войти или зарегистрироваться
          </button>
        </div>
        <LoginModal
          isOpen={isLoginOpen}
          onClose={() => setIsLoginOpen(false)}
          onLogin={handleLoginSubmit}
          onRegister={handleRegisterSubmit}
          redirectPath="/private-circle"
        />
      </>
    );
  }

  const accountEmail = user?.email?.trim() || form.email.trim();

  return (
    <div className="dcc-application" id="application">
      <div className="dcc-application__header">
        <h2>Оставьте заявку на доступ</h2>
        <p>Мы проверим профиль и свяжемся с вами, если формат подходит вашему профессиональному контексту.</p>
        {membershipStatus === 'rejected' && (
          <p className="dcc-form-error" style={{ marginTop: '0.75rem' }}>
            Предыдущая заявка не была одобрена. Вы можете подать новую.
          </p>
        )}
      </div>

      <form className="dcc-form" onSubmit={handleSubmit}>
        <div className="dcc-form__grid">
          <label className="dcc-field">
            <span>Имя</span>
            <input name="firstName" value={form.firstName} onChange={handleChange} disabled={loading} autoComplete="given-name" />
          </label>
          <label className="dcc-field">
            <span>Фамилия</span>
            <input name="lastName" value={form.lastName} onChange={handleChange} disabled={loading} autoComplete="family-name" />
          </label>
        </div>

        <div className="dcc-form__grid">
          <label className="dcc-field">
            <span>Псевдоним</span>
            <input name="nickname" value={form.nickname} onChange={handleChange} disabled={loading} placeholder="Для публичного профиля" />
          </label>
          <label className="dcc-field">
            <span>Email аккаунта</span>
            <input name="email" type="email" value={accountEmail} readOnly disabled className="dcc-field__readonly" />
          </label>
        </div>

        <div className="dcc-form__grid">
          <label className="dcc-field">
            <span>Компания</span>
            <input name="company" value={form.company} onChange={handleChange} disabled={loading} autoComplete="organization" />
          </label>
          <label className="dcc-field">
            <span>Должность</span>
            <input name="title" value={form.title} onChange={handleChange} disabled={loading} autoComplete="organization-title" />
          </label>
        </div>

        <div className="dcc-field">
          <span id="application-sector-label">Сектор</span>
          <CustomSelect
            id="application-sector"
            ariaLabelledBy="application-sector-label"
            value={form.sector}
            onChange={(sector) => {
              setForm({ ...form, sector });
              setError('');
            }}
            options={sectors.map((sector) => ({ value: sector, label: sector }))}
            placeholder="Выберите сектор"
            disabled={loading}
            className="dcc-custom-select"
            triggerClassName="dcc-custom-select__trigger"
            menuClassName="dcc-custom-select__menu"
            optionClassName="dcc-custom-select__option"
          />
        </div>

        <label className="dcc-field dcc-field--phone" htmlFor="application-phone">
          <span>Номер телефона</span>
          <PhoneInput
            id="application-phone"
            value={form.phone}
            disabled={loading}
            onChange={(phone) => {
              setForm((prev) => ({ ...prev, phone }));
              setError('');
            }}
          />
        </label>

        {error && <div className="dcc-form-error">{error}</div>}

        <button className="dcc-button dcc-button--primary" type="submit" disabled={loading}>
          {loading ? 'Отправляем заявку...' : 'Подать заявку'}
        </button>
      </form>

      <p className="dcc-privacy-note">
        Информация используется только для проверки заявки. Данные, по которым можно идентифицировать человека, подвергаются шифрованию и не
        раскрываются в профиле внутри сообщества.
      </p>
    </div>
  );
}

function HeroDashboardPreview() {
  return (
    <div className="dcc-hero-preview" aria-label="Превью продукта">
      <article className="dcc-preview-card dcc-preview-card--compensation">
        <div className="dcc-preview-card__top">
          <div>
            <span className="dcc-card-kicker">Ориентир по компенсациям</span>
            <h3>В рынке ли ваша компенсация?</h3>
          </div>
          <Icon name="chart" />
        </div>
        <div className="dcc-market-labels">
          <span>ниже рынка</span>
          <span>в рынке</span>
          <span>выше рынка</span>
        </div>
        {benchmarkRows.map((row) => (
          <div className="dcc-mini-benchmark" key={row.label}>
            <span>{row.label}</span>
            <div className="dcc-percentile-track">
              <i style={{ left: `${row.position}%` }} />
            </div>
          </div>
        ))}
        <div className="dcc-percentile-scale">
          <span>P25</span>
          <span>P50</span>
          <span>P75</span>
        </div>
      </article>

      <article className="dcc-preview-card dcc-preview-card--role dcc-role-hero-card">
        <div className="dcc-preview-card__top">
          <div>
            <span className="dcc-card-kicker">Рыночный грейд роли</span>
            <h3>Сравнение не по формальному титулу, а по особенностям вашей роли</h3>
          </div>
          <Icon name="layers" />
        </div>
        <div className="dcc-role-flow">
          <span>Аналитик 3</span>
          <b>Ближе к Ассоциат 2</b>
          <span>Ассоциат / Senior Ассоциат</span>
        </div>
      </article>

      <article className="dcc-preview-card dcc-preview-card--peer">
        <div className="dcc-preview-card__top">
          <div>
            <span className="dcc-card-kicker">Рыночные инсайты</span>
            <h3>Анонимные дискуссии с участниками рынка</h3>
          </div>
          <Icon name="network" />
        </div>
        <div className="dcc-sector-tags">
          {['FO', 'PE', 'VC', 'IB', 'M&A', 'Corporate VC'].map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
        <div className="dcc-peer-snippets">
          <p>Какой STI считается рыночным для инвестиционного директора в фонде среднего размера?</p>
          <p>Как оценивать carry для роли уровня Принципала?</p>
        </div>
      </article>
    </div>
  );
}

function HeroSection() {
  return (
    <section className="dcc-hero" id="top">
      <div className="dcc-hero__copy">
        <h1>Узнайте, где вы находитесь относительно рынка</h1>
        <p className="dcc-hero__subtitle">
          Закрытое сообщество для специалистов инвестиционной индустрии: рыночные ориентиры по компенсациям, оценка реального уровня роли и анонимные инсайты от участников рынка.
        </p>
        <p className="dcc-hero__trust">
          Доступ модерируется вручную. Заявки проходят проверку по опыту, сектору и релевантности участия.
        </p>
        <div className="dcc-hero__bullets">
          <span>Узнайте, в рынке ли ваша компенсация</span>
          <span>Сравните вашу роль внутри компании со своим реальным уровнем относительно рынка</span>
          <span>Получайте анонимные инсайты от других участников сообщества</span>
        </div>
        <div className="dcc-hero__coverage">
          <span className="dcc-hero__coverage-label">Покрытие:</span>
          <div className="dcc-hero__coverage-tags">
            {coverageTags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="dcc-hero__form-col">
        <ApplicationForm />
      </div>

      <div className="dcc-hero__preview-col">
        <HeroDashboardPreview />
      </div>
    </section>
  );
}

function CoreValueCards() {
  const cards = [
    {
      icon: 'chart' as IconName,
      title: 'Компенсация',
      headline: 'Проверьте, в рынке ли ваш пакет',
      description: 'Сравнивайте FIX, STI, LTIP и Total Comp с релевантными компаниями и уровнем ролей.',
      visual: 'benchmark',
    },
    {
      icon: 'layers' as IconName,
      title: 'Рыночный уровень роли',
      headline: 'Сравнивайте свою оплату через реальный "вес" вашей роли, а не через формальный титул',
      description: 'Формальное название должности в обзорах зачастую не точно сопоставимо с уровнем отвественности вашей роли - сопоставьте себя точнее',
      visual: 'ladder',
    },
    {
      icon: 'users' as IconName,
      title: 'Рыночные инсайты',
      headline: 'Получайте анонимные инсайты от релевантных участников рынка',
      description: 'Обсуждайте компенсацию, грейды и карьерные ситуации без раскрытия личности.',
      visual: 'nodes',
    },
  ];

  return (
    <section className="dcc-section" id="product">
      <SectionIntro
        title="Три причины вступить в datacircle"
        subtitle="Сообщество объединяет рыночные ориентиры, нормализацию ролей и анонимный обмен опытом между профессионалами инвестиционного рынка."
      />
      <div className="dcc-value-grid">
        {cards.map((card) => (
          <article className="dcc-value-card" key={card.title}>
            <div className="dcc-value-card__icon">
              <Icon name={card.icon} />
            </div>
            <span>{card.title}</span>
            <h3>{card.headline}</h3>
            <p>{card.description}</p>
            {card.visual === 'benchmark' && (
              <div className="dcc-card-range">
                <i />
                <i />
                <b />
              </div>
            )}
            {card.visual === 'ladder' && (
              <div className="dcc-ladder">
                {['Analyst', 'Associate', 'VP', 'Director', 'Partner'].map((level, index) => (
                  <span key={level} className={index === 3 ? 'is-active' : ''}>
                    {level}
                  </span>
                ))}
              </div>
            )}
            {card.visual === 'nodes' && (
              <div className="dcc-node-map">
                {['IB', 'PE', 'VC', 'FO'].map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function CompensationBenchmarkWidget() {
  return (
    <section className="dcc-section" id="benchmarks">
      <SectionIntro title="Сравните ваш компенсационный пакет с актуальными рыночными практиками" subtitle="Бенчмарк по вашему сектору, роли и грейду." />
      <div className="dcc-analytical-card">
        <div className="dcc-filter-row">
          {['PE', 'VC', 'IB', 'M&A', 'FO', 'Corporate VC'].map((filter, index) => (
            <span key={filter} className={index === 0 ? 'is-active' : ''}>
              {filter}
            </span>
          ))}
        </div>
        <div className="dcc-filter-row dcc-filter-row--secondary">
          {['Investment team', 'Director level', 'Москва / СНГ'].map((filter) => (
            <span key={filter}>{filter}</span>
          ))}
        </div>
        <div className="dcc-benchmark-layout">
          <div className="dcc-benchmark-main">
            <div className="dcc-benchmark-scale">
              <span>P25</span>
              <span>P50</span>
              <span>P75</span>
              <span>P90</span>
            </div>
            {benchmarkRows.map((row) => (
              <div className="dcc-benchmark-row" key={row.label}>
                <span className="dcc-benchmark-row__label">{row.label}</span>
                <div className="dcc-benchmark-row__track">
                  <i style={{ left: `${row.position}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function RoleMappingWidget() {
  const [gradingQuestions, setGradingQuestions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void fetchPublicGradingDemoQuestions()
      .then((questions) => {
        if (!cancelled && questions.length) setGradingQuestions(questions);
      })
      .catch(() => {
        /* оставляем пустой список — блок вопросов не показываем */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="dcc-section" id="roles">
      <SectionIntro
        title="Сравнивайте ваш пакет точнее через грейдинг вашей роли, а не через сопоставление формальных титулов"
        subtitle="Грейдинг переводит формальный титул в референтную должность обзора — для точного сравнения с рынком."
      />
      <div className="dcc-role-widget">
        <article className="dcc-role-panel">
          <span>Формальная должность / титул</span>
          <small>Как указано в компании</small>
          <h3>{roleGradingDemo.formalTitle}</h3>
          <p>{roleGradingDemo.formalMeta}</p>
        </article>
        <div className="dcc-connector" aria-hidden="true" />
        <article className="dcc-role-panel dcc-role-panel--accent dcc-role-panel--grading">
          <span>Грейдинг роли</span>
          <small>Пример вопросов из анкеты</small>
          {gradingQuestions.length > 0 ? (
            <ul className="dcc-role-grading-list">
              {gradingQuestions.map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ul>
          ) : (
            <p className="dcc-role-grading-placeholder">Загрузка вопросов из базы…</p>
          )}
        </article>
        <div className="dcc-connector" aria-hidden="true" />
        <article className="dcc-role-panel">
          <span>Референтная должность обзора</span>
          <small>Для сравнения с рыночными данными</small>
          <h3>{roleGradingDemo.referenceTitle}</h3>
          <p>{roleGradingDemo.referenceMeta}</p>
        </article>
      </div>
    </section>
  );
}

function AnonymousPeerInsights() {
  return (
    <section className="dcc-section" id="insights">
      <SectionIntro
        title="Анонимные рыночные инсайты"
        subtitle="Обсуждения проходят внутри релевантных профессиональных контуров — по сектору, роли и уровню."
      />
      <div className="dcc-peer-layout">
        <article className="dcc-cohort-card">
          <div className="dcc-cohort-map">
            {['IB', 'PE', 'VC', 'FO', 'Corporate VC', 'M&A'].map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
          <div className="dcc-quality-tags">
            {['опытные специалисты', 'топ-менеджеры', 'фаундеры', 'investment directors', 'partners'].map((tag) => (
              <b key={tag}>{tag}</b>
            ))}
          </div>
        </article>
        <div className="dcc-insight-list">
          {insightCards.map((card) => (
            <article className="dcc-insight-card" key={card.question}>
              <span>{card.tag}</span>
              <h3>{card.question}</h3>
              <p>{card.meta}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function MemberStats() {
  const metrics = [
    ['120+', 'проверенных участников'],
    ['6', 'сегментов рынка'],
    ['65%', 'Senior+ / Директор+'],
    ['80+', 'анонимных компенсационных профилей'],
    ['40+', 'ролей и уровней'],
    ['2-3 дня', 'обычная проверка заявки'],
  ];

  return (
    <section className="dcc-section dcc-stats-section" id="members">
      <SectionIntro title="Внутри — релевантные участники инвестиционного рынка" />
      <div className="dcc-stats-grid">
        {metrics.map(([value, label]) => (
          <article key={label}>
            <strong>{value}</strong>
            <span>{label}</span>
          </article>
        ))}
      </div>
      <div className="dcc-sector-coverage">
        {['IB', 'M&A', 'PE', 'VC', 'FO', 'Corporate VC', 'Corporate investments'].map((tag) => (
          <span key={tag}>{tag}</span>
        ))}
      </div>
    </section>
  );
}

function HowItWorksTimeline() {
  const steps = [
    ['01', 'Заявка', 'Заполните короткую форму', 'file'],
    ['02', 'Проверка профиля', 'Подтверждаем опыт, сектор и релевантность участия', 'shield'],
    ['03', 'Вводный звонок', 'Короткое знакомство и обсуждение формата участия', 'phone'],
    ['04', 'Доступ', 'Получите доступ к сообществу и рыночным данным', 'lock'],
  ] as const;

  return (
    <section className="dcc-section" id="admission">
      <div className="dcc-section-heading-row">
        <SectionIntro title="Как это работает" subtitle="Пошаговый доступ в закрытое сообщество для профессионалов инвестиционного рынка." />
        <span className="dcc-moderation-badge">
          <Icon name="shield" />
          Ручная модерация
        </span>
      </div>
      <div className="dcc-timeline">
        {steps.map(([num, title, text, icon], index) => (
          <React.Fragment key={num}>
            <article className={index === 1 ? 'is-active' : ''}>
              <div>
                <span>{num}</span>
                <Icon name={icon} />
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
            {index < steps.length - 1 && <i aria-hidden="true" />}
          </React.Fragment>
        ))}
      </div>
      <div className="dcc-micro-trust">
        Ручной отбор помогает сохранить закрытый формат, анонимность и качество профессионального диалога.
      </div>
    </section>
  );
}

function TrustAnonymitySection() {
  const cards = [
    ['Анонимность', 'Ваш профиль будет отображаться без раскрытия имени, компании и компенсации.', 'lock'],
    ['Проверка доступа', 'Каждая заявка проверяется вручную по релевантному опыту, сектору и уровню.', 'shield'],
    ['Модерация среды', 'Обсуждения структурируются по релевантным группам, чтобы обеспечить удобную и быструю навигацию внутри сообщества.', 'users'],
  ] as const;

  return (
    <section className="dcc-section dcc-trust-section" id="trust">
      <SectionIntro
        title="Конфиденциальность — часть продукта"
        subtitle="datacircle строится вокруг проверенных участников, анонимных данных и модерируемого профессионального обмена."
      />
      <div className="dcc-trust-grid">
        {cards.map(([title, text, icon]) => (
          <article key={title}>
            <Icon name={icon} />
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
      <p className="dcc-procedure-note">
        Данные компенсации используются для формирования агрегированных ориентиров и не отображаются как индивидуальные записи.
      </p>
    </section>
  );
}

function EligibilityFAQ() {
  return (
    <section className="dcc-section dcc-eligibility" id="eligibility">
      <div className="dcc-fit-grid">
        <article>
          <h2>Кому подходит</h2>
          <ul>
            <li>специалистам инвестиционных команд</li>
            <li>IB / M&A / PE / VC / FO / Corporate VC</li>
            <li>опытным специалистам, инвестиционным менеджерам, директорам, партнерам и основателям</li>
            <li>тем, кто хочет знать реальный уровень своей роли</li>
            <li>тем, кто готов делиться данными в обмен на актуальную информацию о своем секторе</li>
          </ul>
        </article>
        <article>
          <h2>Кому не подходит</h2>
          <ul>
            <li>рекрутерам и внешним продавцам услуг</li>
            <li>участникам без релевантного опыта в инвестиционной индустрии</li>
            <li>тем, кто ищет открытый карьерный форум</li>
            <li>тем, кто ищет открытый клуб для нетворкинга</li>
            <li>тем, кто не готов соблюдать конфиденциальность</li>
          </ul>
        </article>
      </div>
      <div className="dcc-faq-grid">
        {faqItems.map((item) => (
          <article key={item.question}>
            <h3>{item.question}</h3>
            <p>{item.answer}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="dcc-final-cta">
      <span className="dcc-eyebrow">Доступ по заявке</span>
      <h2>Получите доступ к закрытому профессиональному сообществу для специалистов инвестиционного рынка</h2>
      <p>
        Сравните компенсацию с рынком, оцените реальный уровень роли и получите анонимные инсайты от участников инвестиционной индустрии.
      </p>
      <button type="button" className="dcc-button dcc-button--primary" onClick={() => scrollToSection('application')}>
        Подать заявку
      </button>
      <small>Заявки проходят ручную проверку. Доступ открывается после подтверждения релевантности профиля.</small>
    </section>
  );
}

const PrivateCirclePage: React.FC = () => {
  useEffect(() => {
    if (window.location.hash === '#application') {
      window.requestAnimationFrame(() => scrollToSection('application'));
    }
  }, []);

  return (
    <div className="dcc-page">
      <HeaderNav />
      <main>
        <HeroSection />
        <MemberStats />
        <CoreValueCards />
        <CompensationBenchmarkWidget />
        <RoleMappingWidget />
        <AnonymousPeerInsights />
        <HowItWorksTimeline />
        <TrustAnonymitySection />
        <EligibilityFAQ />
        <FinalCTA />
      </main>
    </div>
  );
};

export default PrivateCirclePage;
